import React from 'react';
import { Container, Box, Typography, Paper, Alert } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { StudentSearchForm } from '../../components/StudentSearch/StudentSearchForm';
import { StudentSearchResults } from '../../components/StudentSearch/StudentSearchResults';
import { StudentDetailModal } from '../../components/StudentSearch/StudentDetailModal';
import { useStudentSearch } from '../../hooks/useStudentSearch';
import { useStudentDetail } from '../../hooks/useStudentDetail';
import { CompanyLayout } from '../../components/Layout';
import { StudentSearchResult } from '../../types/student-search';

const StudentSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { message?: string } | null;

  const {
    students,
    pagination,
    loading,
    error,
    selectedStudents,
    searchFilters,
    searchStudents,
    updateFilters,
    changePage,
    toggleStudentSelection,
    clearSelection,
    resetSearch,
  } = useStudentSearch();

  const {
    student: detailStudent,
    loading: detailLoading,
    isOpen: detailModalOpen,
    openDetail,
    closeDetail,
  } = useStudentDetail();

  const handleSearch = () => {
    searchStudents(searchFilters);
  };

  const handleBulkScout = (studentIds: number[]) => {
    // TODO: 一括スカウト機能実装時に対応
    console.log('Bulk scout to students:', studentIds);
    alert(
      `${studentIds.length}名の学生への一括スカウト送信機能は今後実装予定です`
    );
  };

  const handleSendScout = (studentId: number) => {
    const student = students.find((s) => s.id === studentId);
    if (student) {
      navigate('/company/scout/compose', {
        state: {
          selectedStudent: {
            id: student.id,
            full_name: student.full_name,
            university: student.university,
            faculty: student.faculty,
          },
        },
      });
    }
  };

  const handleSendScoutFromDetail = (student: StudentSearchResult) => {
    navigate('/company/scout/compose', {
      state: {
        selectedStudent: {
          id: student.id,
          full_name: student.full_name,
          university: student.university,
          faculty: student.faculty,
        },
      },
    });
  };

  return (
    <CompanyLayout>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* ページヘッダー */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              color: '#111827',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <SearchIcon sx={{ mr: 1 }} />
            学生検索・一覧
          </Typography>
          <Typography variant="body1" color="text.secondary">
            条件を指定して学生を検索し、スカウト対象を見つけましょう
          </Typography>
        </Box>

        {/* メッセージ表示 */}
        {locationState?.message && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {locationState.message}
          </Alert>
        )}

        {/* エラー表示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 検索フォーム */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <StudentSearchForm
            filters={searchFilters}
            onFiltersChange={updateFilters}
            onSearch={handleSearch}
            onReset={resetSearch}
            loading={loading}
          />
        </Paper>

        {/* 検索結果 */}
        <StudentSearchResults
          students={students}
          pagination={pagination}
          loading={loading}
          selectedStudents={selectedStudents}
          onStudentSelect={toggleStudentSelection}
          onStudentDetail={openDetail}
          onPageChange={changePage}
          onBulkScout={handleBulkScout}
          onSendScout={handleSendScout}
          onClearSelection={clearSelection}
        />

        {/* 学生詳細モーダル */}
        <StudentDetailModal
          open={detailModalOpen}
          student={detailStudent}
          loading={detailLoading}
          onClose={closeDetail}
          onSendScout={handleSendScoutFromDetail}
        />
      </Container>
    </CompanyLayout>
  );
};

export default StudentSearchPage;
